/**
 * Rendering policy for long repertoire scores.
 *
 * Phrase MusicXML may already be downloaded by React, but turning every phrase
 * into thousands of Phaser Graphics objects in one frame can exhaust WebGL.
 * Keep the first usable render small, then extend it as the reader or playback
 * approaches the rendered boundary.
 */
export const REPERTOIRE_INITIAL_PHRASES_DESKTOP = 6;
export const REPERTOIRE_INITIAL_PHRASES_MOBILE = 8;
export const REPERTOIRE_PHRASE_BATCH_SIZE = 4;
export const REPERTOIRE_PLAYBACK_PREFETCH_DISTANCE = 3;
export const REPERTOIRE_VIEWPORT_PREFETCH_DISTANCE = 1.5;

export function getInitialRepertoirePhraseCount(total: number, mobile: boolean): number {
  const limit = mobile ? REPERTOIRE_INITIAL_PHRASES_MOBILE : REPERTOIRE_INITIAL_PHRASES_DESKTOP;
  return Math.min(Math.max(0, total), limit);
}

export function shouldPrefetchRepertoireForPlayback(
  activePhraseIndex: number,
  renderedPhraseCount: number,
  totalPhraseCount: number,
): boolean {
  if (renderedPhraseCount >= totalPhraseCount || renderedPhraseCount === 0) return false;
  return renderedPhraseCount - activePhraseIndex - 1 <= REPERTOIRE_PLAYBACK_PREFETCH_DISTANCE;
}

export function shouldPrefetchRepertoireForViewport(
  scrollPosition: number,
  viewportSize: number,
  contentSize: number,
  renderedPhraseCount: number,
  totalPhraseCount: number,
): boolean {
  if (renderedPhraseCount >= totalPhraseCount || viewportSize <= 0) return false;
  const remaining = Math.max(0, contentSize - (scrollPosition + viewportSize));
  return remaining <= viewportSize * REPERTOIRE_VIEWPORT_PREFETCH_DISTANCE;
}

/** Preserve the full-piece progress denominator while only a subset of
 * render-backed events exists, but trust the event stream if it discovers
 * more playable notes than the backend summary advertised. */
export function getRepertoireProgressNoteCount(
  renderedEventNoteCount: number,
  fullScoreNoteCount: number,
): number {
  return Math.max(0, renderedEventNoteCount, fullScoreNoteCount);
}
